/**
 * ============================================================================
 * /checkin — where the QR code lands. The only participant-facing screen.
 * ============================================================================
 * A participant scans the session code and arrives here with ?s=<session>&k=<code>.
 * They have no staff account (Decision #4). The page exchanges the code for a
 * session token, then asks them to fill in their own block — the digital
 * version of signing the paper roster (Decision #58).
 *
 * SINCE M25 (Decision #64) THE PHONE CAN REMEMBER THEM. The first intake
 * hands the phone a personal key; it is kept in localStorage (it has to
 * outlive the tab, unlike the session token) and sent on every participant
 * request. Next scan at the same institution the backend answers "is this
 * still you?" and the whole intake is one tap. A new phone gets the key by
 * email: "Been here before? Enter your email" → a personal link → /me/<key>.
 * Nobody is ever REQUIRED to have a key; a phone without one gets the form.
 *
 * WHO IS USING THIS, AND WHERE. Firefighters and paramedics, on their own
 * phones, in a bay, often in poor light and sometimes wearing gloves, with
 * thirty other people doing the same thing at once. That drives real decisions
 * here rather than being a note at the bottom:
 *
 *   - the token is kept in sessionStorage, so a reload or an accidental
 *     back-swipe does not make them start again
 *   - the ONE screen they must not lose is the one they have half-filled, so
 *     nothing navigates away from a partly-completed form
 *   - numeric fields carry inputMode so the phone shows a number pad
 *   - the attestation is a real tap every time, with the statement visible —
 *     a pre-ticked box is weak evidence (Decision #53); a tap on "I still
 *     agree" is not
 *   - errors say what to do next, and the answer is usually "ask your
 *     instructor", because that person is standing right there
 *
 * This lives in the admin bundle for now because the QR points at
 * APP_BASE_URL/checkin. H4 may split it into its own PWA; if it does, the
 * contract with the backend is the /api/v1/checkin endpoints and nothing
 * else, so the move should be self-contained.
 * ============================================================================
 */
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams, useParams, Link } from 'react-router-dom';
import { ApiError } from '../lib/api.js';
import {
  TOKEN_KEY,
  PERSONAL_KEY,
  participantFetch,
  readPersonalKey,
  storePersonalKey,
  clearPersonalKey,
} from '../lib/participant.js';
import Field from '../components/Field.jsx';
import Select from '../components/Select.jsx';
import QuestionCard from '../components/QuestionCard.jsx';

const BLANK = {
  external_id: '',
  cert_number: '',
  full_name: '',
  email: '',
  agency_name: '',
  agency_type: '',
  participant_role_title: '',
  worker_type: '',
  waiver_code: '',
  attestation_agreed: false,
};

/**
 * What the backend remembered about them, in the form's shape. `affiliation`
 * is which of their agencies today's class is for (Scene 4); it defaults to
 * the one they used most recently.
 */
function formFromRecognized(r, affiliation) {
  const last = affiliation ?? r?.last ?? {};
  return {
    ...BLANK,
    external_id: r?.external_id ?? '',
    full_name: r?.full_name ?? '',
    email: r?.email ?? '',
    agency_name: last.agency_name ?? '',
    agency_type: last.agency_type ?? '',
    participant_role_title: last.participant_role_title ?? '',
    worker_type: last.worker_type ?? '',
    waiver_code: last.waiver_code ?? '',
    attestation_agreed: false,
  };
}

export function CheckInPage() {
  const [params] = useSearchParams();
  const { code: joinCode } = useParams();      // /j/:code — the read-aloud path
  const sessionId = params.get('s');
  const secret = params.get('k');

  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const [context, setContext] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [hasPersonalKey, setHasPersonalKey] = useState(() => Boolean(readPersonalKey()));

  const [phase, setPhase] = useState('starting'); // starting | form | done | over | dead
  /** On the form phase: the one-tap card, or the full form. */
  const [view, setView] = useState('form'); // card | form
  /** Which of their affiliations today's class is for (Scene 4). Index into recognized.affiliations. */
  const [affiliation, setAffiliation] = useState(0);
  /** "Been here before?" — the email door to a new phone. */
  const [emailAsk, setEmailAsk] = useState({ open: false, email: '', busy: false, sent: null });
  /** 'unconfirmed' when they registered without an A-B Tech ID (Decision #63). */
  const [identityStatus, setIdentityStatus] = useState(null);
  // The live interaction item, polled once the person is checked in.
  const [live, setLive] = useState(null);
  const [answering, setAnswering] = useState(false);
  const [answerNote, setAnswerNote] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const set = useCallback((patch) => setForm((f) => ({ ...f, ...patch })), []);

  /** Apply what /session said. Shared by first load and "the link was tapped". */
  const applyContext = useCallback((ctx) => {
    setContext(ctx);
    if (ctx.intake_complete) {
      setForm((f) => ({ ...f, full_name: ctx.full_name ?? f.full_name }));
      setIdentityStatus(ctx.identity_status ?? null);
      setPhase('done');
      return;
    }
    if (ctx.recognized) {
      setAffiliation(0);
      setForm(formFromRecognized(ctx.recognized, ctx.recognized.affiliations?.[0]));
      setView('card');
    } else {
      setView('form');
    }
    setPhase('form');
  }, []);

  // --- exchange the code for a token, then load what we are checked in to ---
  useEffect(() => {
    let active = true;

    const hasCode = Boolean(joinCode || (sessionId && secret));

    async function begin(stored) {
      let current = stored;
      try {
        if (!current) {
          // Two doors into the same room: the QR (?s=&k=) and the short code
          // someone read aloud or pasted into a chat (/j/CODE).
          if (!joinCode && (!sessionId || !secret)) {
            throw new ApiError(
              400,
              'NO_CODE',
              'This page needs a session code. Scan the QR, or ask your instructor for the join code.'
            );
          }
          const issued = joinCode
            ? await participantFetch('/api/v1/checkin/join', {
                method: 'POST',
                body: JSON.stringify({ code: joinCode }),
              })
            : await participantFetch('/api/v1/checkin', {
                method: 'POST',
                body: JSON.stringify({ session_id: sessionId, secret }),
              });
          current = issued.token;
          sessionStorage.setItem(TOKEN_KEY, current);
          if (!active) return;
          setToken(current);
        }

        const ctx = await participantFetch('/api/v1/checkin/session', { token: current });
        if (!active) return;
        // Already filled the form? Go straight back to the live question. A
        // reload, a slept phone or an accidental back-swipe must not drop
        // somebody onto a form they already completed.
        applyContext(ctx);
      } catch (err) {
        if (!active) return;
        // A stored token that no longer works must not trap the page — drop it
        // so a fresh scan can recover.
        if (err.code === 'SESSION_TOKEN_REJECTED') {
          sessionStorage.removeItem(TOKEN_KEY);
          if (stored && current === stored) {
            // A token this tab was already holding. If they have just scanned
            // a code, that code is the room they want: exchange it afresh.
            if (hasCode) return begin(null);
            // Otherwise they were in that room and came back to the tab; the
            // usual reason the token stopped working is that the class closed,
            // and "couldn't check you in" would read as if they never came.
            setPhase('over');
            return;
          }
        }
        setError(err);
        setPhase('dead');
      }
    }

    begin(token);
    return () => {
      active = false;
    };
    // Runs once: re-running would mint a second token for the same person.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * The email link opens in a NEW tab, so this tab never sees the key arrive
   * — except that localStorage fires a `storage` event in every other tab on
   * the origin. Listen for it while the form is still ahead of them and ask
   * the backend again: the card appears here without anyone scanning twice.
   */
  const recheck = useCallback(async () => {
    if (!token) return;
    try {
      const ctx = await participantFetch('/api/v1/checkin/session', { token });
      setHasPersonalKey(Boolean(readPersonalKey()));
      applyContext(ctx);
    } catch {
      /* a failed recheck just leaves the form where it is */
    }
  }, [token, applyContext]);

  useEffect(() => {
    if (phase !== 'form') return undefined;
    const onStorage = (e) => {
      if (e.key === PERSONAL_KEY && e.newValue) recheck();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [phase, recheck]);

  // Decided by the SECTION, not by this page. A section that is not making an
  // FTE funding claim has no reason to ask for a Colleague ID or a waiver code
  // — see intakeProfileFor() in the backend.
  const minimal = context?.intake_profile === 'minimal';
  const reference = context?.reference;
  const attestation = reference?.attestation;
  const recognized = context?.recognized ?? null;
  /**
   * Empty until paid-or-volunteer is answered — not "all four".
   *
   * This screen labels the codes by service only ("Fire" / "Rescue / EMS"),
   * because a participant should not have to know what CEPFR means. But all
   * four codes collapse onto those two labels, so showing them unfiltered
   * renders "Fire, Fire, Rescue, Rescue". The control is disabled until the
   * question above is answered anyway, so an empty list is the honest state.
   */
  const waiverOptions = form.worker_type
    ? (reference?.waiver_codes ?? []).filter((w) => w.workerType === form.worker_type)
    : [];

  /**
   * Once someone is checked in, their phone watches for whatever the instructor
   * pushes. Polled for the same reason the roster is: it survives a locked
   * phone, a slept screen and a blink of signal, with nothing to reconnect.
   *
   * A failed poll is swallowed — a momentary drop must not replace a question
   * someone is halfway through answering with an error.
   */
  useEffect(() => {
    if (phase !== 'done' || !token) return undefined;
    let active = true;

    const tick = () =>
      participantFetch('/api/v1/checkin/question', { token })
        .then((data) => active && setLive(data))
        .catch((err) => {
          // The one failure that is NOT a blip: the instructor closed the
          // class, and closing is what ends every check-in token in the room
          // (Brian, 22 Sep: the phone still showed the class after close).
          if (active && err?.code === 'SESSION_TOKEN_REJECTED') {
            sessionStorage.removeItem(TOKEN_KEY);
            setLive(null);
            setPhase('over');
          }
        });

    tick();
    const timer = setInterval(tick, 2500);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [phase, token]);

  async function sendAnswer(answer) {
    setAnswering(true);
    setAnswerNote(null);
    try {
      await participantFetch('/api/v1/checkin/answer', {
        token,
        method: 'POST',
        body: JSON.stringify({
          question_id: live.question.id,
          // Idempotency the schema was built for: a retry after a dropped
          // connection is a no-op rather than a duplicate.
          client_uuid: crypto.randomUUID(),
          answer,
        }),
      });
      setAnswerNote('Answer sent.');
      setLive((l) => (l ? { ...l, already_answered: true } : l));
    } catch (err) {
      setAnswerNote(err.message);
    } finally {
      setAnswering(false);
    }
  }

  /** One intake body, whether it came from the card's single tap or the form. */
  function intakeBody(values) {
    if (minimal) {
      return {
        // The minimal schema is .strict(), so sending anything else here is a
        // rejection rather than a silent drop. That is deliberate.
        person: {
          full_name: values.full_name.trim(),
          email: values.email.trim() || null,
          cert_number: values.cert_number?.trim() || null,
        },
        enrollment: {
          agency_name: values.agency_name.trim(),
          participant_role_title: values.participant_role_title.trim() || null,
        },
      };
    }
    return {
      person: {
        // Optional (Decision #63). Blank = registered as an unconfirmed
        // participant; the office confirms it later. Ignored by the backend
        // when the phone is recognised — the ID on file is the office's.
        external_id: values.external_id.trim() || null,
        // The state certification number (P#): how a System person is
        // recognised at the door when the phone does not know them (M46).
        cert_number: values.cert_number?.trim() || null,
        full_name: values.full_name.trim(),
        email: values.email.trim() || null,
      },
      enrollment: {
        agency_name: values.agency_name.trim(),
        agency_type: values.agency_type || null,
        participant_role_title: values.participant_role_title.trim() || null,
        worker_type: values.worker_type || null,
        // A WAIVER IS CLAIMED ONLY WHEN THE PERSON ACTUALLY CLAIMED IT.
        // This used to be hardcoded 'waived', so anyone who left the
        // attestation unticked was refused at the door for missing
        // paperwork — the exact thing M15 exists to prevent. Without a
        // signed attestation the enrolment is left 'pending' (the server
        // default): they are in the room and on the roster, and the fee
        // question is settled afterwards (participation-not-gated-on-paperwork).
        ...(values.attestation_agreed && values.waiver_code ? { fee_status: 'waived' } : {}),
        waiver_code: values.waiver_code || null,
        attestation_agreed: values.attestation_agreed,
      },
    };
  }

  async function sendIntake(values) {
    setError(null);
    setSubmitting(true);
    try {
      const result = await participantFetch('/api/v1/checkin/intake', {
        token,
        method: 'POST',
        body: JSON.stringify(intakeBody(values)),
      });
      // The phone is handed its key exactly once. Keep it: next scan is one tap.
      if (result?.participant_key) {
        storePersonalKey(result.participant_key);
        setHasPersonalKey(true);
      }
      setForm(values);
      setIdentityStatus(result?.identity_status ?? null);
      setPhase('done');
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  }

  function submit(event) {
    event.preventDefault();
    return sendIntake(form);
  }

  /** The card's single tap: everything on file, plus today's attestation. */
  function checkInAsRecognized() {
    return sendIntake({ ...form, attestation_agreed: !minimal });
  }

  async function askByEmail(event) {
    event.preventDefault();
    setEmailAsk((s) => ({ ...s, busy: true }));
    try {
      const result = await participantFetch('/api/v1/checkin/recognize', {
        token,
        method: 'POST',
        body: JSON.stringify({ email: emailAsk.email.trim() }),
      });
      setEmailAsk((s) => ({ ...s, busy: false, sent: result?.message ?? 'If we know you, a link is on its way.' }));
    } catch (err) {
      setEmailAsk((s) => ({ ...s, busy: false, sent: err.message }));
    }
  }

  async function forgetThisPhone() {
    try {
      await participantFetch('/api/v1/checkin/forget', { method: 'POST' });
    } catch {
      /* the key is cleared locally either way */
    }
    clearPersonalKey();
    setHasPersonalKey(false);
    if (phase === 'form') {
      setForm(BLANK);
      setView('form');
      setContext((c) => (c ? { ...c, recognized: null } : c));
    }
  }

  const section = context?.section;
  const title = section?.official_ccl_title || section?.title;
  const code =
    section?.course_prefix && section?.course_number
      ? `${section.course_prefix} ${section.course_number}${
          section.section_number ? `-${section.section_number}` : ''
        }`
      : section?.ccl_course_code;

  // Only where it is true in THIS room: a phone can hold a key for another
  // institution (or a revoked one) and still be a stranger here, and telling
  // that person "this phone remembers you" above a blank form is a lie.
  const forgetFooter = hasPersonalKey && (recognized || phase === 'done') && (
    <p className="muted checkin__footer">
      This phone remembers you for next time.{' '}
      <button type="button" className="btn btn--quiet" onClick={forgetThisPhone} disabled={submitting}>
        Forget me on this phone
      </button>
    </p>
  );

  if (phase === 'starting') {
    return (
      <main className="checkin" aria-busy="true">
        <p className="muted">Checking you in…</p>
      </main>
    );
  }

  // The class closed under them (or they came back to the tab afterwards).
  // Say so, and point at the one place the result will show up.
  if (phase === 'over') {
    return (
      <main className="checkin">
        <div className="card">
          <h1>This class is over</h1>
          {(form.full_name || title) && (
            <p>
              {form.full_name && <strong>{form.full_name}</strong>}
              {form.full_name && title && ' — '}
              {title}
              {code && ` (${code})`}
            </p>
          )}
          <p>Your instructor has closed the class. Your attendance is recorded.</p>
          {hasPersonalKey ? (
            <p>
              <Link to="/me" className="btn btn--primary">See my record</Link>
            </p>
          ) : null}
          <p className="muted">
            Credit shows on your record once the instructor signs off — usually the same day.
            {!hasPersonalKey && ' Your instructor or the training office can confirm it for you.'}
          </p>
        </div>
        {forgetFooter}
      </main>
    );
  }

  if (phase === 'dead') {
    return (
      <main className="checkin">
        <div className="card">
          <h1>Couldn’t check you in</h1>
          <p>{error?.message}</p>
          <p className="muted">
            Scan the code again from the start. If it still doesn’t work, your instructor can
            check you in directly.
          </p>
        </div>
      </main>
    );
  }

  if (phase === 'done') {
    return (
      <main className="checkin">
        <div className="card">
          <h1>You’re checked in ✓</h1>
          <p>
            <strong>{form.full_name}</strong>
            {title && (
              <>
                {' '}
                — {title}
                {code && ` (${code})`}
              </>
            )}
          </p>
          {identityStatus === 'unconfirmed' && (
            <p className="callout callout--warn">
              <strong>You’re in — no student ID yet.</strong>
              <span className="callout__detail">
                You’ll get credit for today. The office will match you to your A-B Tech record
                before it reaches your transcript.
              </span>
            </p>
          )}
          <p className="muted">
            Keep this page open — questions will appear here during the session.
          </p>
          {hasPersonalKey && (
            <p className="card__footer">
              <Link to="/me">See my record</Link> — it updates when your instructor signs off.
            </p>
          )}
        </div>

        {answerNote && (
          <p className="banner banner--success" role="status">
            {answerNote}
          </p>
        )}

        {live?.question ? (
          live.already_answered ? (
            <div className="card">
              <h2>{live.question.stem}</h2>
              <p className="muted">Your answer is in. Waiting for the next question…</p>
            </div>
          ) : (
            <div className="card">
              <QuestionCard question={live.question} disabled={answering} onSubmit={sendAnswer} />
            </div>
          )
        ) : (
          <p className="muted" aria-live="polite">
            Nothing on screen right now. This page will update when your instructor asks
            something.
          </p>
        )}

        {forgetFooter}
      </main>
    );
  }

  // --- the one-tap card ------------------------------------------------------
  // The phone presented a key and the backend named a person at this
  // institution. Everything is on file; the only thing that is NOT carried
  // over is the attestation, which is fee-waiver evidence and has to be a tap
  // today with the statement in view.
  if (view === 'card' && recognized) {
    return (
      <main className="checkin">
        <header className="checkin__head">
          <h1>Is this still you?</h1>
          {title && (
            <p className="muted">
              {title}
              {code && ` · ${code}`}
            </p>
          )}
        </header>

        {error && (
          <p className="banner banner--error" role="alert">
            {error.message}
          </p>
        )}

        <div className="card recognized">
          <p className="recognized__name">{form.full_name}</p>
          {(recognized.affiliations?.length ?? 0) > 1 ? (
            // More than one agency on file (Scene 4). The attestation below
            // names "the public safety agency listed", so the choice is theirs
            // and it is made in the open, not carried over silently.
            <fieldset className="recognized__aff">
              <legend className="muted">Checking in for</legend>
              {recognized.affiliations.map((a, i) => (
                <label key={a.agency_name} className="checkbox recognized__aff-option">
                  <input
                    type="radio"
                    name="affiliation"
                    checked={affiliation === i}
                    onChange={() => {
                      setAffiliation(i);
                      setForm(formFromRecognized(recognized, a));
                    }}
                    disabled={submitting}
                  />
                  <span>
                    <strong>{a.agency_name}</strong>
                    {[a.participant_role_title, a.worker_type === 'paid' ? 'Paid' : a.worker_type === 'volunteer' ? 'Volunteer' : null]
                      .filter(Boolean)
                      .map((t) => ` · ${t}`)
                      .join('')}
                  </span>
                </label>
              ))}
            </fieldset>
          ) : (
            <p className="recognized__line">
              {[form.agency_name, form.participant_role_title].filter(Boolean).join(' · ') || (
                <span className="muted">No agency on file yet</span>
              )}
            </p>
          )}
          {!minimal && (
            <p className="recognized__line">
              {recognized.identity_status === 'unconfirmed' || !form.external_id ? (
                <span className="muted">No student ID on file yet — the office will confirm it</span>
              ) : (
                <>A-B Tech ID {form.external_id}</>
              )}
            </p>
          )}
          <p className="card__footer">
            <button
              type="button"
              className="btn btn--quiet"
              onClick={() => {
                setError(null);
                setView('form');
              }}
              disabled={submitting}
            >
              Something changed — edit
            </button>
          </p>
        </div>

        {!minimal && attestation && (
          <fieldset className="attestation">
            <legend>Please confirm</legend>
            <blockquote className="attestation__text">“{attestation.text}”</blockquote>
            <p className="muted">{attestation.note}</p>
          </fieldset>
        )}

        <button
          type="button"
          className="btn btn--primary"
          onClick={checkInAsRecognized}
          disabled={submitting}
        >
          {submitting ? 'Checking you in…' : !minimal && attestation ? 'I still agree — check in' : 'Check in'}
        </button>

        {forgetFooter}
      </main>
    );
  }

  return (
    <main className="checkin">
      <header className="checkin__head">
        <h1>Check in</h1>
        {title && (
          <p className="muted">
            {title}
            {code && ` · ${code}`}
          </p>
        )}
      </header>

      {error && (
        <p className="banner banner--error" role="alert">
          {error.message}
        </p>
      )}

      {/* The new-phone door. Only offered when nothing is on file for this
          phone — a recognised person editing their details has no use for it. */}
      {!recognized && !emailAsk.open && (
        <p className="muted checkin__before">
          Been here before?{' '}
          <button
            type="button"
            className="btn btn--quiet"
            onClick={() => setEmailAsk((s) => ({ ...s, open: true }))}
            disabled={submitting}
          >
            Enter your email
          </button>{' '}
          and we’ll fill this in for you.
        </p>
      )}

      {!recognized && emailAsk.open && (
        <form className="card recognize" onSubmit={askByEmail}>
          <h2>Been here before?</h2>
          {emailAsk.sent ? (
            <>
              <p role="status">{emailAsk.sent}</p>
              <p className="muted">
                Open the link on this phone, then come back here.{' '}
                <button type="button" className="btn btn--quiet" onClick={recheck}>
                  I’ve opened it
                </button>
              </p>
            </>
          ) : (
            <>
              <Field
                label="The email you gave last time"
                type="email"
                value={emailAsk.email}
                onChange={(v) => setEmailAsk((s) => ({ ...s, email: v }))}
                autoComplete="email"
                inputMode="email"
                disabled={emailAsk.busy}
              />
              <button type="submit" className="btn btn--primary" disabled={emailAsk.busy || !emailAsk.email.trim()}>
                {emailAsk.busy ? 'Sending…' : 'Send me a link'}
              </button>
            </>
          )}
          <p className="card__footer">
            <button
              type="button"
              className="btn btn--quiet"
              onClick={() => setEmailAsk({ open: false, email: '', busy: false, sent: null })}
            >
              Never mind, I’ll fill in the form
            </button>
          </p>
        </form>
      )}

      <form onSubmit={submit}>
        {!minimal && !recognized && (
          <Field
            label="A-B Tech student ID"
            value={form.external_id}
            onChange={(v) => set({ external_id: v })}
            hint="Don’t know it? Leave it blank — you’ll still be checked in. The office will match you to your record afterwards."
            inputMode="numeric"
            autoComplete="off"
            required={false}
            disabled={submitting}
          />
        )}
        {!minimal && recognized && (
          <p className="field">
            <span className="field__label">A-B Tech student ID</span>
            <br />
            {form.external_id ? (
              <strong>{form.external_id}</strong>
            ) : (
              <span className="muted">Not on file yet — the office will confirm it.</span>
            )}
            <br />
            <span className="field__hint">
              {form.external_id ? 'Wrong ID? Tell the office; it can’t be changed from here.' : ''}
            </span>
          </p>
        )}

        {!recognized && (
          <Field
            label="State certification number (P#)"
            value={form.cert_number}
            onChange={(v) => set({ cert_number: v })}
            hint="The number on your NC EMS card, like P012345. It finds your record — leave it blank if you don’t have it to hand."
            autoComplete="off"
            autoCapitalize="characters"
            required={false}
            disabled={submitting}
          />
        )}

        <Field
          label="Your name"
          value={form.full_name}
          onChange={(v) => set({ full_name: v })}
          autoComplete="name"
          disabled={submitting}
        />

        <Field
          label="Your email"
          type="email"
          value={form.email}
          onChange={(v) => set({ email: v })}
          hint="So a new phone can find you next time. Nothing else is sent to it."
          autoComplete="email"
          inputMode="email"
          required={false}
          disabled={submitting}
        />

        <Field
          label="Your agency"
          value={form.agency_name}
          onChange={(v) => set({ agency_name: v })}
          hint="The department or service you work for."
          disabled={submitting}
        />

        <Field
          label="Your job title"
          value={form.participant_role_title}
          onChange={(v) => set({ participant_role_title: v })}
          required={false}
          hint="Paramedic, Fire Fighter, Patrol Officer, Dispatcher…"
          disabled={submitting}
        />

        {!minimal && (
          <>
        <Select
          label="Agency type"
          value={form.agency_type}
          onChange={(v) => set({ agency_type: v })}
          options={(reference?.agency_types ?? []).map((t) => ({
            value: t,
            label: t.charAt(0).toUpperCase() + t.slice(1),
          }))}
          required={false}
          disabled={submitting}
        />

        <Select
          label="Are you paid or a volunteer?"
          value={form.worker_type}
          onChange={(v) => set({ worker_type: v, waiver_code: '' })}
          options={[
            { value: 'paid', label: 'Paid' },
            { value: 'volunteer', label: 'Volunteer' },
          ]}
          required={false}
          optional={false}
          disabled={submitting}
        />

        <Select
          label="Fire or rescue?"
          value={form.waiver_code}
          onChange={(v) => set({ waiver_code: v })}
          options={waiverOptions.map((w) => ({
            value: w.code,
            label: w.service === 'fire' ? 'Fire' : 'Rescue / EMS',
          }))}
          placeholder={form.worker_type ? 'Choose…' : 'Answer the question above first…'}
          required={false}
          optional={false}
          hint="This decides which fee waiver applies to you."
          disabled={submitting || !form.worker_type}
        />

          </>
        )}

        {!minimal && attestation && (
          <fieldset className="attestation">
            <legend>Please confirm</legend>
            <blockquote className="attestation__text">“{attestation.text}”</blockquote>
            <p className="muted">{attestation.note}</p>

            <label className="checkbox">
              <input
                type="checkbox"
                checked={form.attestation_agreed}
                onChange={(e) => set({ attestation_agreed: e.target.checked })}
                disabled={submitting}
              />
              <span>I agree to the statement above.</span>
            </label>
          </fieldset>
        )}

        <button type="submit" className="btn btn--primary" disabled={submitting}>
          {submitting ? 'Checking you in…' : 'Check in'}
        </button>
      </form>

      {forgetFooter}
    </main>
  );
}

/**
 * /me/:key — where the email link lands (M25).
 *
 * Stores the key and says so. It cannot continue to the check-in on its own:
 * a QR needs the session's secret as well as its id, and the email carries
 * neither (a link that could check someone in by itself would be a link worth
 * stealing). The original check-in tab, if still open, notices the key arrive
 * through the storage event and shows the card; otherwise they scan again and
 * are recognised.
 */
export function RememberMePage() {
  const { key } = useParams();
  const [params] = useSearchParams();
  const [stored, setStored] = useState(null);

  useEffect(() => {
    // Only a key-shaped key is kept (43 chars of base64url, M25). Anything
    // else — a mistyped link, a path that merely starts with /me/ — must not
    // overwrite the key this phone already holds.
    if (!key || !/^[A-Za-z0-9_-]{43}$/.test(key)) {
      setStored(false);
      return;
    }
    storePersonalKey(key);
    setStored(readPersonalKey() === key);
  }, [key]);

  const cameFromSession = Boolean(params.get('s'));

  return (
    <main className="checkin">
      <div className="card">
        {stored === false ? (
          <>
            <h1>That link didn’t work</h1>
            <p className="muted">
              This phone couldn’t keep the link. You can still check in by scanning the class
              code and filling in the form.
            </p>
          </>
        ) : (
          <>
            <h1>This phone now knows you ✓</h1>
            <p>
              {cameFromSession
                ? 'Go back to the check-in page — it will recognize you now. If it doesn’t, scan the class code again.'
                : 'Scan the class code to check in. Your details will already be filled in.'}
            </p>
            <p className="card__footer">
              <Link to="/me">See my record</Link>
            </p>
          </>
        )}
      </div>
    </main>
  );
}

export default CheckInPage;
